import axios from 'axios';
import { API_BASE_URL } from '../config/environment';

// Remove trailing slash from base URL to prevent double-slash issues
const API_URL = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;

// ==========================================
// INTERFACES
// ==========================================

export interface OSTemplate {
  id: string;
  family: string;
  version: string;
  display_name: string;
  code_name: string | null;
  description: string | null;
  logo_url: string | null;
  min_cpu: number;
  min_ram_mb: number;
  min_disk_gb: number;
  cloud_image_url: string;
  cloud_image_format: string;
  default_username: string;
  supports_cloud_init: boolean;
  is_active: boolean;
  is_featured: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  host_statuses?: HostStatus[];
}

export interface HostStatus {
  host_id: number;
  host_name: string;
  status: 'pending' | 'downloading' | 'creating' | 'ready' | 'failed' | 'outdated';
  vmid: number | null;
  synced_at: string | null;
}

export interface OSTemplateFamily {
  family: string;
  display_name: string;
  logo_url: string | null;
  templates: OSTemplate[];
}

export interface HostTemplate {
  id: string;
  host_id: number;
  template_id: string;
  proxmox_template_vmid: number | null;
  storage_pool: string;
  status: 'pending' | 'downloading' | 'creating' | 'ready' | 'failed' | 'outdated';
  sync_started_at: string | null;
  synced_at: string | null;
  error_message: string | null;
  template_family?: string;
  template_version?: string;
  template_display_name?: string;
  host_name?: string;
}

export interface HostTemplateStatus {
  host_id: number;
  host_name: string;
  datacenter_location: string;
  status: string;
  templates: HostTemplate[];
}

export interface CreateOSTemplateData {
  family: string;
  version: string;
  display_name: string;
  code_name?: string;
  description?: string;
  logo_url?: string;
  min_cpu?: number;
  min_ram_mb?: number;
  min_disk_gb?: number;
  cloud_image_url: string;
  cloud_image_format?: string;
  default_username?: string;
  supports_cloud_init?: boolean;
  is_active?: boolean;
  is_featured?: boolean;
  sort_order?: number;
}

export interface UpdateOSTemplateData extends Partial<CreateOSTemplateData> {}

export interface TemplateStats {
  totalTemplates: number;
  activeTemplates: number;
  totalHostTemplates: number;
  readyHostTemplates: number;
  pendingHostTemplates: number;
  failedHostTemplates: number;
}

// ==========================================
// AUTH HEADER
// ==========================================

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

// ==========================================
// ADMIN OPERATIONS
// ==========================================

export const getAllTemplates = async (activeOnly: boolean = false, withHostStatus: boolean = false): Promise<OSTemplate[]> => {
  const params = new URLSearchParams();
  if (activeOnly) params.append('active_only', 'true');
  if (withHostStatus) params.append('with_host_status', 'true');

  const response = await axios.get(
    `${API_URL}/api/admin/os-templates?${params.toString()}`,
    getAuthHeader()
  );
  return response.data.data || [];
};

export const getTemplatesByFamily = async (activeOnly: boolean = true): Promise<OSTemplateFamily[]> => {
  const response = await axios.get(
    `${API_URL}/api/admin/os-templates/by-family?active_only=${activeOnly}`,
    getAuthHeader()
  );
  return response.data.data || [];
};

export const getTemplate = async (id: string): Promise<OSTemplate> => {
  const response = await axios.get(`${API_URL}/api/admin/os-templates/${id}`, getAuthHeader());
  return response.data.data;
};

export const createTemplate = async (data: CreateOSTemplateData): Promise<OSTemplate> => {
  const response = await axios.post(`${API_URL}/api/admin/os-templates`, data, getAuthHeader());
  return response.data.data;
};

export const updateTemplate = async (id: string, data: UpdateOSTemplateData): Promise<OSTemplate> => {
  const response = await axios.put(`${API_URL}/api/admin/os-templates/${id}`, data, getAuthHeader());
  return response.data.data;
};

export const deleteTemplate = async (id: string): Promise<void> => {
  await axios.delete(`${API_URL}/api/admin/os-templates/${id}`, getAuthHeader());
};

export const getTemplateStats = async (): Promise<TemplateStats> => {
  const response = await axios.get(`${API_URL}/api/admin/os-templates/stats`, getAuthHeader());
  return response.data.data;
};

// ==========================================
// HOST TEMPLATE OPERATIONS
// ==========================================

export const getHostTemplateStatus = async (hostId: number): Promise<HostTemplate[]> => {
  const response = await axios.get(
    `${API_URL}/api/admin/os-templates/host/${hostId}/status`,
    getAuthHeader()
  );
  return response.data.data || [];
};

export const getAllHostsTemplateStatus = async (): Promise<HostTemplateStatus[]> => {
  const response = await axios.get(
    `${API_URL}/api/admin/os-templates/hosts/status`,
    getAuthHeader()
  );
  return response.data.data || [];
};

export const initializeHostTemplate = async (
  templateId: string,
  hostId: number,
  storagePool: string = 'local-zfs'
): Promise<HostTemplate> => {
  const response = await axios.post(
    `${API_URL}/api/admin/os-templates/${templateId}/init/host/${hostId}`,
    { storage_pool: storagePool },
    getAuthHeader()
  );
  return response.data.data;
};

export const syncTemplateToHost = async (
  templateId: string,
  hostId: number,
  storagePool: string = 'local-zfs'
): Promise<{ message: string }> => {
  const response = await axios.post(
    `${API_URL}/api/admin/os-templates/${templateId}/sync/host/${hostId}`,
    { storage_pool: storagePool },
    getAuthHeader()
  );
  return response.data;
};

export const syncTemplateToAllHosts = async (
  templateId: string,
  storagePool: string = 'local-zfs'
): Promise<{ message: string; data: any[] }> => {
  const response = await axios.post(
    `${API_URL}/api/admin/os-templates/${templateId}/sync/all-hosts`,
    { storage_pool: storagePool },
    getAuthHeader()
  );
  return response.data;
};

export const syncAllTemplatesToHost = async (
  hostId: number,
  storagePool: string = 'local-zfs'
): Promise<{ message: string; data: any[] }> => {
  const response = await axios.post(
    `${API_URL}/api/admin/os-templates/sync-all/host/${hostId}`,
    { storage_pool: storagePool },
    getAuthHeader()
  );
  return response.data;
};

export const updateHostTemplateStatus = async (
  templateId: string,
  hostId: number,
  status: string,
  vmid?: number,
  errorMessage?: string
): Promise<HostTemplate> => {
  const response = await axios.patch(
    `${API_URL}/api/admin/os-templates/${templateId}/host/${hostId}/status`,
    { status, vmid, error_message: errorMessage },
    getAuthHeader()
  );
  return response.data.data;
};

// ==========================================
// PUBLIC OPERATIONS (for VPS creation)
// ==========================================

export const getAvailableTemplates = async (): Promise<OSTemplateFamily[]> => {
  const response = await axios.get(`${API_URL}/api/os-templates/available`);
  return response.data.data || [];
};

export const getAvailableTemplatesForHost = async (hostId: number): Promise<OSTemplateFamily[]> => {
  const response = await axios.get(`${API_URL}/api/os-templates/available/host/${hostId}`);
  return response.data.data || [];
};

export const validateTemplateForHost = async (
  templateId: string,
  hostId: number
): Promise<{ is_available: boolean; vmid: number | null }> => {
  const response = await axios.get(
    `${API_URL}/api/os-templates/validate/${templateId}/host/${hostId}`
  );
  return response.data.data;
};

// ==========================================
// UTILITY FUNCTIONS
// ==========================================

export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'ready':
      return 'text-green-600 bg-green-100';
    case 'downloading':
    case 'creating':
      return 'text-blue-600 bg-blue-100';
    case 'pending':
      return 'text-gray-600 bg-gray-100';
    case 'failed':
      return 'text-red-600 bg-red-100';
    case 'outdated':
      return 'text-yellow-600 bg-yellow-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
};

export const getStatusIcon = (status: string): string => {
  switch (status) {
    case 'ready':
      return '✅';
    case 'downloading':
      return '⬇️';
    case 'creating':
      return '🔧';
    case 'pending':
      return '⏳';
    case 'failed':
      return '❌';
    case 'outdated':
      return '🔄';
    default:
      return '❓';
  }
};

export const getFamilyLogo = (family: string): string => {
  const logos: Record<string, string> = {
    ubuntu: '/images/os/ubuntu.svg',
    debian: '/images/os/debian.svg',
    almalinux: '/images/os/almalinux.svg',
    rocky: '/images/os/rocky.svg',
    fedora: '/images/os/fedora.svg',
    centos: '/images/os/centos.svg'
  };
  return logos[family] || '/images/os/linux.svg';
};
